import { BrushBehavior, brushY, D3BrushEvent, ScaleContinuousNumeric, select } from 'd3'
import usePrevious from 'hooks/usePrevious'
import { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components/macro'

const Handle = styled.circle`
  fill: white;
  stroke: rgba(0, 0, 0, 0.14);
  stroke-width: 1;
  filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.2));
  pointer-events: none;
`

const RangeIndicator = styled.rect`
  pointer-events: none;
`

const CenterHandle = styled.rect`
  fill: white;
  stroke: rgba(0, 0, 0, 0.14);
  stroke-width: 1;
  filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.18));
  pointer-events: none;
`

const HandleLabel = styled.text`
  fill: ${({ theme }) => theme.textSecondary};
  font-size: 10px;
  font-weight: 600;
  pointer-events: none;
`

const BRUSH_MARGIN = 2
const INDICATOR_WIDTH = 16
const MINIMUM_INDICATOR_HEIGHT = 40

const toPixels = (extent: [number, number], yScale: ScaleContinuousNumeric<number, number>): [number, number] => [
  yScale(extent[1]),
  yScale(extent[0]),
]

const compare = (
  first: [number, number],
  second: [number, number],
  yScale: ScaleContinuousNumeric<number, number>
): boolean =>
  toPixels(first, yScale).every((value, index) => value.toFixed(1) === toPixels(second, yScale)[index].toFixed(1))

export function VerticalBrush({
  id,
  yScale,
  interactive,
  brushExtent,
  setBrushExtent,
  innerWidth,
  innerHeight,
  lowerHandleColor,
  upperHandleColor,
  brushLabels,
}: {
  id: string
  yScale: ScaleContinuousNumeric<number, number>
  interactive: boolean
  brushExtent: [number, number]
  setBrushExtent: (extent: [number, number], mode: string | undefined) => void
  innerWidth: number
  innerHeight: number
  lowerHandleColor: string
  upperHandleColor: string
  brushLabels: (direction: 'w' | 'e', value: number) => string
}) {
  const brushRef = useRef<SVGGElement | null>(null)
  const behavior = useRef<BrushBehavior<SVGGElement> | null>(null)
  const [localExtent, setLocalExtent] = useState(brushExtent)
  const previousExtent = usePrevious(brushExtent)

  const brushed = useCallback(
    (event: D3BrushEvent<unknown>) => {
      if (!event.selection) return
      const [top, bottom] = event.selection as [number, number]
      const extent: [number, number] = [yScale.invert(bottom), yScale.invert(top)]
      setLocalExtent(extent)

      if (event.type === 'end' && !compare(brushExtent, extent, yScale)) {
        setBrushExtent(extent, event.mode)
      }
    },
    [brushExtent, setBrushExtent, yScale]
  )

  useEffect(() => setLocalExtent(brushExtent), [brushExtent])

  useEffect(() => {
    if (!brushRef.current) return

    behavior.current = brushY<SVGGElement>()
      .extent([
        [0, BRUSH_MARGIN],
        [innerWidth, innerHeight - BRUSH_MARGIN],
      ])
      .handleSize(28)
      .filter(() => interactive)
      .on('brush end', brushed)

    const group = select(brushRef.current)
    group.call(behavior.current as any)
    group.selectAll('.selection').attr('stroke', 'none').attr('fill', 'transparent').attr('fill-opacity', 1)
    group.selectAll('.handle').attr('fill', 'transparent').attr('stroke', 'transparent')

    if (previousExtent && compare(brushExtent, previousExtent, yScale)) {
      group.call(behavior.current.move as any, toPixels(brushExtent, yScale))
    }
  }, [brushExtent, brushed, id, innerHeight, innerWidth, interactive, previousExtent, yScale])

  useEffect(() => {
    if (brushRef.current && behavior.current) {
      behavior.current.move(select(brushRef.current) as any, toPixels(brushExtent, yScale) as any)
    }
  }, [brushExtent, yScale])

  const [top, bottom] = toPixels(localExtent, yScale)
  const rangeIsVisible = bottom >= 0 && top <= innerHeight
  const clampedTop = Math.max(BRUSH_MARGIN, Math.min(innerHeight - BRUSH_MARGIN, top))
  const clampedBottom = Math.max(BRUSH_MARGIN, Math.min(innerHeight - BRUSH_MARGIN, bottom))
  const naturalHeight = Math.max(0, clampedBottom - clampedTop)
  const indicatorHeight = Math.min(innerHeight - BRUSH_MARGIN * 2, Math.max(naturalHeight, MINIMUM_INDICATOR_HEIGHT))
  const indicatorCenter = (clampedTop + clampedBottom) / 2
  const indicatorTop = Math.max(
    BRUSH_MARGIN,
    Math.min(innerHeight - BRUSH_MARGIN - indicatorHeight, indicatorCenter - indicatorHeight / 2)
  )
  const indicatorBottom = indicatorTop + indicatorHeight
  const centerY = (indicatorTop + indicatorBottom) / 2
  const indicatorX = innerWidth - INDICATOR_WIDTH

  return (
    <>
      <defs>
        <linearGradient id={`${id}-vertical-selection`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop stopColor={upperHandleColor} />
          <stop stopColor={lowerHandleColor} offset="1" />
        </linearGradient>
      </defs>
      <g ref={brushRef} />
      {rangeIsVisible && (
        <RangeIndicator
          x={indicatorX}
          y={indicatorTop}
          width={INDICATOR_WIDTH}
          height={indicatorHeight}
          rx={INDICATOR_WIDTH / 2}
          fill={`url(#${id}-vertical-selection)`}
        />
      )}
      {top >= 0 && top <= innerHeight && <Handle cx={innerWidth - INDICATOR_WIDTH / 2} cy={top} r={6} />}
      {bottom >= 0 && bottom <= innerHeight && <Handle cx={innerWidth - INDICATOR_WIDTH / 2} cy={bottom} r={6} />}
      {rangeIsVisible && interactive && (
        <>
          <CenterHandle x={innerWidth - 14} y={centerY - 3} width="12" height="6" rx="2" />
          {[0, 1, 2].map((line) => (
            <line
              key={line}
              x1={innerWidth - 9 + line * 2}
              x2={innerWidth - 9 + line * 2}
              y1={centerY - 1.5}
              y2={centerY + 1.5}
              stroke="rgba(0,0,0,0.35)"
              strokeWidth="0.6"
              pointerEvents="none"
            />
          ))}
        </>
      )}
      {top >= 0 && top <= innerHeight && (
        <HandleLabel x={innerWidth - INDICATOR_WIDTH - 7} y={Math.max(11, top - 7)} textAnchor="end">
          {brushLabels('e', localExtent[1])}
        </HandleLabel>
      )}
      {bottom >= 0 && bottom <= innerHeight && (
        <HandleLabel x={innerWidth - INDICATOR_WIDTH - 7} y={Math.min(innerHeight - 3, bottom + 13)} textAnchor="end">
          {brushLabels('w', localExtent[0])}
        </HandleLabel>
      )}
      {top < 0 && (
        <polygon
          points={`${innerWidth - 14},9 ${innerWidth - 2},9 ${innerWidth - 8},2`}
          fill={upperHandleColor}
          pointerEvents="none"
        />
      )}
      {bottom > innerHeight && (
        <polygon
          points={`${innerWidth - 14},${innerHeight - 9} ${innerWidth - 2},${innerHeight - 9} ${innerWidth - 8},${
            innerHeight - 2
          }`}
          fill={lowerHandleColor}
          pointerEvents="none"
        />
      )}
    </>
  )
}
